import { useState, useCallback } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import type { OrderedFlowItem } from './listFlowOrdering'
import { GripVerticalIcon } from '../components/Icons'
import './FlowOrderPopup.css'

export interface FlowOrderPopupProps {
  flowName: string
  droppedItem: OrderedFlowItem
  ghostItems: OrderedFlowItem[]
  /** Name of the real list item above the drop zone, or null for top of list */
  prevNeighborName: string | null
  /** Name of the real list item below the drop zone, or null for bottom of list */
  nextNeighborName: string | null
  /** Called on every reorder within the popup (immediate apply) */
  onReorder: (orderedItemIds: string[]) => void
  /** Called when popup should close (back or done) */
  onClose: () => void
}

interface PopupSortableItemProps {
  item: OrderedFlowItem
  isDragOverlay?: boolean
}

function PopupSortableItem({ item, isDragOverlay }: PopupSortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flow-order-popup-item ${item.isGhost ? 'flow-order-popup-ghost' : 'flow-order-popup-real'} ${isDragging ? 'flow-order-popup-dragging' : ''} ${isDragOverlay ? 'flow-order-popup-overlay' : ''}`}
      {...attributes}
      {...listeners}
    >
      <span className="flow-order-popup-grip" aria-hidden="true"><GripVerticalIcon size={16} /></span>
      <span className="flow-order-popup-name">{item.name}</span>
    </div>
  )
}

export function FlowOrderPopup({
  flowName,
  droppedItem,
  ghostItems,
  prevNeighborName,
  nextNeighborName,
  onReorder,
  onClose,
}: FlowOrderPopupProps) {
  const allItems: OrderedFlowItem[] = [droppedItem, ...ghostItems]
  const [orderedIds, setOrderedIds] = useState<string[]>(allItems.map(i => i.id))
  const [activeDragId, setActiveDragId] = useState<string | null>(null)

  const itemsById = new Map(allItems.map(i => [i.id, i]))

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string)
  }

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveDragId(null)
    const { active, over } = event
    if (!over || active.id === over.id) return

    setOrderedIds(prev => {
      const oldIndex = prev.indexOf(active.id as string)
      const newIndex = prev.indexOf(over.id as string)
      if (oldIndex === -1 || newIndex === -1) return prev
      const next = arrayMove(prev, oldIndex, newIndex)
      // Apply immediately on every reorder
      onReorder(next)
      return next
    })
  }, [onReorder])

  const dragItem = activeDragId ? itemsById.get(activeDragId) : null

  return (
    <div className="flow-order-popup-overlay-root" role="presentation">
      <div className="flow-order-popup-backdrop" aria-hidden="true" />
      <div className="flow-order-popup-container" role="dialog" aria-modal="true">
      <div className="flow-order-popup-header">
        <h2 className="flow-order-popup-title">{flowName} order</h2>
        <button
          type="button"
          className="flow-order-popup-close flow-order-popup-close-desktop"
          onClick={onClose}
          aria-label="Close"
        >
          Close
        </button>
      </div>

      <div className="flow-order-popup-description">
        Move items here for more precise placement in the flow.
      </div>

      <div className="flow-order-popup-list">
        {/* Top neighbor or edge marker */}
        <div className="flow-order-popup-neighbor">
          {prevNeighborName
            ? <span className="flow-order-popup-neighbor-name">{prevNeighborName}</span>
            : <span className="flow-order-popup-edge-label">Top of the list</span>
          }
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
            {orderedIds.map(id => {
              const item = itemsById.get(id)
              if (!item) return null
              return <PopupSortableItem key={id} item={item} />
            })}
          </SortableContext>

          <DragOverlay>
            {dragItem ? (
              <PopupSortableItem item={dragItem} isDragOverlay />
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Bottom neighbor or edge marker */}
        <div className="flow-order-popup-neighbor">
          {nextNeighborName
            ? <span className="flow-order-popup-neighbor-name">{nextNeighborName}</span>
            : <span className="flow-order-popup-edge-label">Bottom of the list</span>
          }
        </div>
      </div>

      <div className="flow-order-popup-footer">
        <button
          type="button"
          className="flow-order-popup-close flow-order-popup-close-mobile"
          onClick={onClose}
          aria-label="Close"
        >
          Close
        </button>
      </div>
      </div>
    </div>
  )
}
